import type { BlogPost } from "@/hooks/useBlogPosts";

export type ExportContentOptions = {
  includeTitle: boolean;
  includeBody: boolean;
  includeSocialMediaScript: boolean;
  includeIntroAndTags: boolean;
};

const defaultExportOptions: ExportContentOptions = {
  includeTitle: true,
  includeBody: true,
  includeSocialMediaScript: true,
  includeIntroAndTags: true,
};

/**
 * Strips HTML tags and converts to plain text
 */
function htmlToPlainText(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

/**
 * Exports blog posts to a downloadable .txt file
 * Includes: title, tags, plain text content, and social media script
 */
export function exportBlogPostsToTxt(
  posts: BlogPost[],
  options: ExportContentOptions = defaultExportOptions,
): void {
  const content = posts
    .map((post) => {
      const plainContent = htmlToPlainText(post.content || "");
      const previewText = htmlToPlainText(post.preview || "");
      const tags =
        post.post_tags
          ?.map((pt) => pt.tags?.name)
          .filter(Boolean)
          .join(", ") || "No tags";
      const sectionBlocks: string[] = [`${"=".repeat(50)}`];

      if (options.includeTitle) {
        sectionBlocks.push(`TITLE: ${post.title}`);
      }

      if (options.includeIntroAndTags) {
        if (previewText) {
          sectionBlocks.push(`INTRO: ${previewText}`);
        }
        sectionBlocks.push(`TAGS: ${tags}`);
      }

      if (options.includeBody) {
        sectionBlocks.push(`CONTENT:\n${plainContent}`);
      }

      if (options.includeSocialMediaScript && post.social_media_script) {
        sectionBlocks.push(`SOCIAL MEDIA SCRIPT:\n${post.social_media_script}`);
      }

      sectionBlocks.push(`${"=".repeat(50)}`);

      const postSection = sectionBlocks.join("\n\n");

      return postSection;
    })
    .join("\n\n");

  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `blog-posts-export-${new Date().toISOString().split("T")[0]}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
